import { ref, computed, watch } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import moment from 'moment-timezone'
import store from '@/store'
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import { debounce } from 'lodash'
import useFilterCounter from '@/components/Filters/useFiltersCount'
import { SOURCES, SLOT_TYPES } from "@/config/layoutConfig";
import i18n from '@/libs/i18n'

export default function useList() {
  const refListTable = ref(null)
  const toast = useToast()
  const { t } = useI18nUtils();

  store.dispatch('tenant/getAll');
  const tenantOptions = computed(() => store.getters['tenant/getSelectedItems']);

  const tableColumns = [
    { label: t('action_plan.validated'), key: 'validated' },
    { label: t('action_plan.validatedDate'), key: 'validated_at' },
    { label: t('action_plan.answeredDate'), key: 'answered_at' },
    { label: t('action_plan.openedDate'), key: "opened_at" },
    { label: t('action_plan.list.cols.col1'), key: 'action_plan.name', sortable: false },
    { label: t('action_plan.list.cols.col2'), key: 'user.email', sortable: false },
    { label: t('action_plan.deadline'), key: 'deadline', sortable: false },
    { label: t('action_plan.userValidated'), key: 'user_validation.name' },
    { label: t('action_plan.list.cols.col3'), key: 'action_plan.tenant.name', sortable: false },
    { label: t('action_plan.list.cols.col4'), key: 'id_alarm', sortable: false },
    { label: t('action_plan.list.cols.col5'), key: 'created_at', sortable: false },
  ]

  const perPage = ref(10)
  const totalList = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref('id')
  const isSortDirDesc = ref(true)
  const selectedTenants = ref([])
  const startDatetimeFilter = ref(null)
  const endDatetimeFilter = ref(null)
  const lastLogSelected = ref({});
  const selectedObject = ref(null);

  const dataMeta = computed(() => {
    const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalList.value,
    }
  })

  const fetchList = (ctx, callback) => {
    store.dispatch('action_plan/getLogs', {
      q: searchQuery.value,
      max: perPage.value,
      'page[n]': currentPage.value,
      sortBy: sortBy.value,
      sortDesc: isSortDirDesc.value,
      id_tenant: selectedTenants.value,
      dt_from: startDatetimeFilter.value,
      dt_to: endDatetimeFilter.value,
    })
      .then(response => {
        callback(response.data);
        totalList.value = response.meta.total;

        if (lastLogSelected.value) {
          updateItemSelected(response.data);
        }
      })
      .catch((e) => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error obteniendo la información',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const formatDateAssigned = value => {
    if(value != null){
      const formattedDate = moment(value).format('YYYY-MM-DD HH:mm:ss')
      return formattedDate
    }
  }

  const calculateDaysDifference = (deadline) => {
    if (!deadline) return null;
    const today = moment().startOf('day');
    const deadlineDate = moment(deadline).startOf('day');
    return deadlineDate.diff(today, 'days');
  }

  const getDeadlineTooltip = (deadline) => {
    if (!deadline) return '';
    const daysDiff = calculateDaysDifference(deadline);

    const translateWithParams = (key, params) => {
      try {
        return i18n.t(key, params);
      } catch (e) {
        let text = t(key);
        if (params && typeof text === 'string') {
          Object.keys(params).forEach(paramKey => {
            text = text.replace(new RegExp(`\\{${paramKey}\\}`, 'g'), params[paramKey]);
          });
        }
        return text;
      }
    };

    if (daysDiff < 0) {
      return translateWithParams('action_plan.deadlineOverdue', { days: Math.abs(daysDiff) });
    } else if (daysDiff === 0) {
      return translateWithParams('action_plan.deadlineToday');
    } else {
      return translateWithParams('action_plan.deadlineRemaining', { days: daysDiff });
    }
  }

  const refetchData = () => {
    refListTable.value.refresh()
  }

  watch([
    currentPage,
    perPage,
  ], () => {
    refetchData()
  })

  watch([searchQuery], debounce(() => {
    refetchData()
  }, 500))

  // filters counter ============================
  const filters = ref({});
  const filtersCount = useFilterCounter(filters);
  const buildFilters = () => {
    filters.value = {
      id_tenant: selectedTenants.value,
      dt_from: startDatetimeFilter.value,
      dt_to: endDatetimeFilter.value,
    };
    refetchData();
  }
  const clearSearchFilters = () => {
    selectedTenants.value = null;
    startDatetimeFilter.value = null;
    endDatetimeFilter.value = null;
    buildFilters();
  }
  // ============================================

  const updateItemSelected = (items) => {
    if (selectedObject.value != null) {
      const itemSelected = items.find(item => item.uuid === selectedObject.value.data.uuid_action_plan_log);

      selectedObject.value.data.validated_at = itemSelected.validated_at;
      selectedObject.value.data.user_validation = itemSelected.user_validation
    }
  }

  return {
    tableColumns,
    perPage,
    currentPage,
    totalList,
    fetchList,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refListTable,
    formatDateAssigned,
    refetchData,
    tenantOptions,
    selectedTenants,
    startDatetimeFilter,
    endDatetimeFilter,
    filtersCount,
    buildFilters,
    clearSearchFilters,
    lastLogSelected,
    selectedObject,
    calculateDaysDifference,
    getDeadlineTooltip
  }
}
